import { Entity, EntityLink } from '../../entity';
import { PlannedItemConstructionRule } from '../planned.item/planned.item';
import { PlannedTaskConstructionRule } from '../planned.task/planned.task';
import { TaskApprovalStatus } from './task.approval';

/** Bumped when the evidence shape or the construction rules it records change meaning. */
export const JOB_CONSTRUCTION_EVIDENCE_VERSION = 2;

export enum JobConstructionAuthorisationKind {
    Manual = 'Manual',
    AutoApproved = 'AutoApproved',
    /** Construction ran without standing authority; recorded rather than dressed up as an approval. */
    Unauthorised = 'Unauthorised',
}

/** Joins the immutable approval entry; the snapshot itself is never copied. */
export interface JobConstructionManualAuthorisation {
    kind: JobConstructionAuthorisationKind.Manual;
    actor?: Entity;
    approvedAt?: Date;
    approvedTasks?: number;
    /** Version 1 only; a Job built before task-native stamps. */
    approvalId?: string;
    evidenceEntryId?: string;
    revision?: number;
}

/** Auto-approval has no record to point at, so the evaluated policy and its time are the evidence. */
export interface JobConstructionAutoAuthorisation {
    kind: JobConstructionAuthorisationKind.AutoApproved;
    serviceLineId: string;
    policyEvaluatedAt: Date;
}

export interface JobConstructionUnauthorised {
    kind: JobConstructionAuthorisationKind.Unauthorised;
    standing: TaskApprovalStatus;
    evaluatedAt: Date;
}

export type JobConstructionAuthorisation = JobConstructionManualAuthorisation | JobConstructionAutoAuthorisation | JobConstructionUnauthorised;

export interface JobConstructionRuleApplied<TRule> {
    sourceId: string;
    rule: TRule;
}

/** One contributing occurrence. `sourceHash` + `resolverVersion` name the exact effective work built. */
export interface JobConstructionSource {
    occurrenceId: string;
    localDate?: string;
    /** Renders the approval time in the occurrence's own calendar, not the server's. */
    timezone?: string;
    serviceLine?: Entity;
    servicePlan?: Entity;
    authorisation: JobConstructionAuthorisation;
    sourceHash: string;
    resolverVersion: string;
    tasks: JobConstructionRuleApplied<PlannedTaskConstructionRule>[];
    items: JobConstructionRuleApplied<PlannedItemConstructionRule>[];
    /** Rows beyond the per-source cap; counted so a capped record never reads as complete. */
    omittedTasks: number;
    omittedItems: number;
}

export interface JobConstructionEvidence {
    version: typeof JOB_CONSTRUCTION_EVIDENCE_VERSION;
    sources: JobConstructionSource[];
}

/**
 * What Visit construction hands the ordinary Job-creation pipeline, so the standard Job-created activity
 * is the one creation entry: its name is the summary, and it carries the evidence and Visit links.
 */
export interface JobCreateConstruction {
    summary: string;
    evidence: JobConstructionEvidence;
    links: EntityLink[];
}
